/**
 * Points + miniatures synchronisés avec <product-slider> (scroll mobile / grille desktop).
 */
class HvGallery extends HTMLElement {
  constructor() {
    super();
    this.bound = false;
    this.slider = null;
    this.onSync = this.onSync.bind(this);
    this.onSlideChanged = this.onSlideChanged.bind(this);
    this.onChromeClick = this.onChromeClick.bind(this);
  }

  connectedCallback() {
    this.bind();
  }

  disconnectedCallback() {
    this.unbind();
  }

  visibleSlides() {
    return Array.from(this.slider.querySelectorAll('[id^="Slide-"]')).filter(function (el) {
      return el.clientWidth > 0;
    });
  }

  isScrollCarousel() {
    return this.slider.scrollWidth > this.slider.clientWidth + 4;
  }

  slideStep() {
    var slides = this.visibleSlides();
    if (slides.length < 2) return this.slider.clientWidth || 1;
    return slides[1].offsetLeft - slides[0].offsetLeft;
  }

  activeIndex() {
    var slides = this.visibleSlides();
    if (!slides.length) return 0;

    if (this.isScrollCarousel()) {
      var step = this.slideStep();
      if (!step) return 0;
      var idx = Math.round(this.slider.scrollLeft / step);
      if (idx < 0) idx = 0;
      if (idx >= slides.length) idx = slides.length - 1;
      return idx;
    }

    var active = this.slider.querySelector('.product-images__slide.is-active');
    var idx = active ? slides.indexOf(active) : 0;
    return idx >= 0 ? idx : 0;
  }

  setActive(index) {
    this.querySelectorAll('.hv-gallery__dot').forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
    this.querySelectorAll('.hv-gallery__thumb-item').forEach(function (item, i) {
      item.classList.toggle('is-active', i === index);
    });
  }

  scrollToIndex(index) {
    var slides = this.visibleSlides();
    var el = slides[index];
    if (!el) return;

    if (this.isScrollCarousel()) {
      var step = this.slideStep();
      this.slider.scrollTo({ left: index * step, behavior: 'smooth' });
      return;
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }

  onSync() {
    if (!this.slider) return;
    this.setActive(this.activeIndex());
  }

  onSlideChanged(e) {
    var page = e.detail && typeof e.detail.currentPage === 'number' ? e.detail.currentPage - 1 : null;
    if (page !== null && !Number.isNaN(page)) {
      this.setActive(page);
    } else {
      this.onSync();
    }
  }

  onChromeClick(e) {
    var t = e.target.closest('.hv-gallery__dot, .hv-gallery__thumb-item');
    if (!t || !this.contains(t)) return;
    var dots = Array.from(this.querySelectorAll('.hv-gallery__dot'));
    var thumbs = Array.from(this.querySelectorAll('.hv-gallery__thumb-item'));
    var idx = dots.indexOf(t);
    if (idx === -1) idx = thumbs.indexOf(t);
    if (idx === -1) return;
    e.preventDefault();
    this.scrollToIndex(idx);
  }

  bind() {
    if (this.bound) return;
    this.slider = this.querySelector('product-slider');
    if (!this.slider) return;

    var dots = this.querySelectorAll('.hv-gallery__dot');
    var thumbs = this.querySelectorAll('.hv-gallery__thumb-item');
    if (!dots.length && !thumbs.length) return;

    this.bound = true;

    this.slider.addEventListener('scroll', this.onSync, { passive: true });
    this.slider.addEventListener('scrollend', this.onSync, { passive: true });
    this.slider.addEventListener('slideChanged', this.onSlideChanged);
    this.addEventListener('click', this.onChromeClick);

    this.resizeObserver = new ResizeObserver(this.onSync);
    this.resizeObserver.observe(this.slider);

    this.mutationObserver = new MutationObserver(this.onSync);
    this.mutationObserver.observe(this.slider, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    this.onSync();
  }

  unbind() {
    if (!this.bound || !this.slider) return;

    this.slider.removeEventListener('scroll', this.onSync);
    this.slider.removeEventListener('scrollend', this.onSync);
    this.slider.removeEventListener('slideChanged', this.onSlideChanged);
    this.removeEventListener('click', this.onChromeClick);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    this.slider = null;
    this.bound = false;
  }
}

if (!customElements.get('hv-gallery')) {
  customElements.define('hv-gallery', HvGallery);
}
